import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  CheckSquare,
  Calendar,
  MoreHorizontal,
  FileSpreadsheet,
} from "lucide-react";
import customSelectStyles from "../../../../components/ui/customSelectStyles";
import MobileAdvancedFilterSheet from "../../../../components/ui/MobileAdvancedFilterSheet";
import { getAllBranches } from "../../../../api/hrm/branches/info";
import {
  getAllDepartments,
  getDepartmentsByBranch,
} from "../../../../api/hrm/departments/departmentInfo";
import { getEmployeesFilter } from "../../../../api/hrm/employees/employeeInfo";
import { getTaskTags } from "../../../../api/hrm/tasks/task_tag_info";
import { hasPermission, canExportHrmTasksData } from "../../../../utils/permissionUtils";
import { useDebounce } from "use-debounce";

const TEXT = {
  searchPlaceholder: "Tìm theo nhân viên hoặc công việc",
  refresh: "Làm mới",
  advanced: "Lọc nâng cao",
  advancedTitle: "Bộ lọc nâng cao",
  branch: "Chi nhánh",
  department: "Bộ phận",
  employee: "Nhân viên",
  status: "Trạng thái",
  taskType: "Loại công việc",
  tag: "Nhãn công việc",
  fromDate: "Từ ngày",
  toDate: "Đến ngày",
  monthPicker: "Chọn tháng",
  clear: "Xóa lọc",
  apply: "Áp dụng",
  assign: "Giao việc",
  register: "Đăng ký công việc",
  exportExcel: "Kết xuất Excel (.xlsx)",
  exportSummaryExcel: "Kết xuất tổng hợp (.xlsx)",
  exportModalTitle: "Kết xuất danh sách công việc",
  exportSummaryModalTitle: "Kết xuất tổng hợp công việc",
  exportModalHint:
    "Khoảng ngày xuất file (mặc định theo tháng đang lọc). Chi nhánh, bộ phận và từ khóa áp dụng theo bộ lọc hiện tại.",
  exportSummaryModalHint:
    "Tổng hợp theo nhân viên: số CV đăng ký/giao/báo cáo, tỷ lệ báo cáo/ngày công, và đánh giá đủ/thiếu (số ngày chấm công không có công việc).",
  exportFromDate: "Từ ngày",
  exportToDate: "Đến ngày",
  cancel: "Hủy",
  downloadXlsx: "Tải file .xlsx",
};

const STATUS_OPTIONS = [
  { value: "waiting", label: "Chờ duyệt" },
  { value: "approved", label: "Đã duyệt" },
  { value: "reported", label: "Đã báo cáo" },
  { value: "completed", label: "Hoàn thành" },
  { value: "rejected", label: "Không duyệt" },
  { value: "rejected-report", label: "Chưa đạt" },
];

const TASK_TYPE_OPTIONS = [
  { value: "register", label: "Đăng ký" },
  { value: "assigned", label: "Giao việc" },
];

const emptyAdvanced = {
  branch_id: "",
  department_id: "",
  employee_id: "",
  status: "",
  task_type: "",
  tag_id: "",
};

const formatDateLocal = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from_date: formatDateLocal(first),
    to_date: formatDateLocal(last),
  };
};

const TaskFilter = ({
  onFilterChange,
  onAssignClick,
  onCreateClick,
  onExportXlsxRequest,
  onExportSummaryXlsxRequest,
}) => {
  const menuRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("detail");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [filters, setFilters] = useState({
    keyword: "",
    ...emptyAdvanced,
    from_date: "",
    to_date: "",
  });

  const [debouncedKeyword] = useDebounce(filters.keyword, 500);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    if (debouncedKeyword !== undefined) {
      onFilterChange?.({ ...filtersRef.current, keyword: debouncedKeyword });
    }
  }, [debouncedKeyword]);

  const monthValue = filters.from_date ? filters.from_date.slice(0, 7) : "";

  const hasAdvancedFilters = useMemo(
    () =>
      Boolean(
        filters.branch_id ||
          filters.department_id ||
          filters.employee_id ||
          filters.status ||
          filters.task_type ||
          filters.tag_id
      ),
    [filters]
  );

  const advancedActiveCount = useMemo(() => {
    let n = 0;
    if (filters.branch_id) n += 1;
    if (filters.department_id) n += 1;
    if (filters.employee_id) n += 1;
    if (filters.status) n += 1;
    if (filters.task_type) n += 1;
    if (filters.tag_id) n += 1;
    return n;
  }, [filters]);

  const handleMonthChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    const [y, m] = v.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    const updated = {
      ...filters,
      from_date: formatDateLocal(first),
      to_date: formatDateLocal(last),
    };
    setFilters(updated);
    onFilterChange?.(updated);
  };

  const monthInputClassName =
    "shrink-0 rounded-2xl border-2 border-transparent bg-gray-50 px-3 text-sm font-medium text-gray-800 outline-none transition-all focus:border-red-500 focus:bg-white focus:ring-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100";

  useEffect(() => {
    const defaults = {
      keyword: "",
      ...emptyAdvanced,
      ...getDefaultMonthRange(),
    };
    setFilters(defaults);
    onFilterChange?.(defaults);
  }, []);

  useEffect(() => {
    const checkTime = () => {
      const [hour, minute] = new Date()
        .toLocaleTimeString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh", hour12: false })
        .split(":")
        .map(Number);
      const total = hour * 60 + minute;
      setIsAllowed(total >= 450 && total <= 1260);
    };
    checkTime();
    const timer = setInterval(checkTime, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    getAllBranches()
      .then((res) => {
        const branches = res.items || res;
        setBranchOptions(
          (Array.isArray(branches) ? branches : []).map((b) => ({
            value: b.id,
            label: b.name,
          }))
        );
      })
      .catch(console.error);

    getTaskTags()
      .then((res) => {
        const tags = Array.isArray(res) ? res : res?.items || [];
        setTagOptions(
          tags.map((t) => ({
            value: t.id,
            label: t.tag_name || t.name,
          }))
        );
      })
      .catch(console.error);
  }, []);

  const loadDepartments = useCallback(async (branchId) => {
    try {
      if (branchId) {
        const res = await getDepartmentsByBranch(branchId);
        const depts = res.items || res || [];
        setDepartmentOptions(
          (Array.isArray(depts) ? depts : []).map((d) => ({
            value: d.id,
            label: d.name,
          }))
        );
      } else {
        const res = await getAllDepartments();
        const depts = res.items || res || [];
        setDepartmentOptions(
          (Array.isArray(depts) ? depts : []).map((d) => ({
            value: d.id,
            label: d.name,
          }))
        );
      }
    } catch (err) {
      console.error(err);
      setDepartmentOptions([]);
    }
  }, []);

  const loadEmployees = useCallback(async (branchId, departmentId) => {
    try {
      const list = await getEmployeesFilter({
        branch_id: branchId || undefined,
        department_id: departmentId || undefined,
      });
      const rows = Array.isArray(list) ? list : list?.items || [];
      setEmployeeOptions(
        rows.map((e) => ({
          value: e.id,
          label: e.name || e.employee_name || e.email || "—",
        }))
      );
    } catch (err) {
      console.error(err);
      setEmployeeOptions([]);
    }
  }, []);

  useEffect(() => {
    loadDepartments(filters.branch_id);
  }, [filters.branch_id, loadDepartments]);

  useEffect(() => {
    loadEmployees(filters.branch_id, filters.department_id);
  }, [filters.branch_id, filters.department_id, loadEmployees]);

  const selectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };

    if (name === "branch_id") {
      updated.department_id = "";
      updated.employee_id = "";
    }
    if (name === "department_id") {
      updated.employee_id = "";
    }

    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = {
      keyword: "",
      ...emptyAdvanced,
      ...getDefaultMonthRange(),
    };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const ActionButton = ({ icon: Icon, onClick, title, active = false, badge = 0 }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "border-red-200 bg-red-50 text-red-600 shadow-sm"
          : "border-gray-200 bg-white text-gray-500 hover:border-red-300 hover:text-red-600"
      }`}
    >
      <Icon size={18} strokeWidth={2} />
      {badge > 0 && (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-black text-white">
          {badge}
        </span>
      )}
    </button>
  );

  const handleRefresh = () => clearFilters();

  const canExport =
    canExportHrmTasksData() &&
    (typeof onExportXlsxRequest === "function" ||
      typeof onExportSummaryXlsxRequest === "function");

  const openExportModal = (mode = "detail") => {
    setExportMode(mode);
    setExportFrom(filters.from_date || "");
    setExportTo(filters.to_date || "");
    setShowExportModal(true);
    setShowMenu(false);
  };

  const runExport = () => {
    if (!exportFrom || !exportTo) return;
    if (exportFrom > exportTo) return;
    const payload = {
      from_date: exportFrom,
      to_date: exportTo,
      keyword: filters.keyword,
      branch_id: filters.branch_id || undefined,
      department_id: filters.department_id || undefined,
      employee_id: filters.employee_id || undefined,
      status: filters.status || undefined,
      task_type: filters.task_type || undefined,
      tag_id: filters.tag_id || undefined,
    };
    if (exportMode === "summary") {
      onExportSummaryXlsxRequest?.(payload);
    } else {
      onExportXlsxRequest?.(payload);
    }
    setShowExportModal(false);
  };

  const exportModal = (
    <AnimatePresence>
      {showExportModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4"
          onClick={() => setShowExportModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-1 text-lg font-bold text-gray-800">
              {exportMode === "summary" ? TEXT.exportSummaryModalTitle : TEXT.exportModalTitle}
            </h3>
            <p className="mb-4 text-sm text-gray-500">
              {exportMode === "summary" ? TEXT.exportSummaryModalHint : TEXT.exportModalHint}
            </p>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{TEXT.exportFromDate}</label>
                <input
                  type="date"
                  value={exportFrom}
                  onChange={(e) => setExportFrom(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-400"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{TEXT.exportToDate}</label>
                <input
                  type="date"
                  value={exportTo}
                  onChange={(e) => setExportTo(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-400"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                className="flex-1 rounded-lg border border-gray-300 py-2.5 font-medium text-gray-700 hover:bg-gray-50"
              >
                {TEXT.cancel}
              </button>
              <button
                type="button"
                disabled={!exportFrom || !exportTo || exportFrom > exportTo}
                onClick={runExport}
                className="flex-1 rounded-lg bg-red-600 py-2.5 font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {TEXT.downloadXlsx}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const FieldLabel = ({ children }) => (
    <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
      {children}
    </label>
  );

  const SelectField = ({ label, options, value, onChange, placeholder = "— Chọn —", className = "" }) => (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <FieldLabel>{label}</FieldLabel>
      <Select
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        value={options.find((opt) => opt.value === value) || null}
        onChange={(opt) => onChange(opt?.value || "")}
        isClearable
        menuPortalTarget={document.body}
      />
    </div>
  );

  const DateField = ({ label, value, onChange, max, min, className = "" }) => (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <FieldLabel>{label}</FieldLabel>
      <input
        type="date"
        value={value || ""}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-gray-200 bg-white px-3 text-sm font-medium text-gray-800 outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-100"
      />
    </div>
  );

  const AdvancedFields = () => (
    <div className="flex flex-wrap items-end gap-x-3 gap-y-4">
      <SelectField
        label={TEXT.branch}
        options={branchOptions}
        value={filters.branch_id}
        onChange={(v) => updateFilter("branch_id", v)}
        className="w-full min-w-[11rem] sm:w-[14rem]"
      />
      <SelectField
        label={TEXT.department}
        options={departmentOptions}
        value={filters.department_id}
        onChange={(v) => updateFilter("department_id", v)}
        className="w-full min-w-[11rem] sm:w-[14rem]"
      />
      <SelectField
        label={TEXT.employee}
        options={employeeOptions}
        value={filters.employee_id}
        onChange={(v) => updateFilter("employee_id", v)}
        className="w-full min-w-[12rem] sm:w-[16rem]"
      />
      <SelectField
        label={TEXT.status}
        options={STATUS_OPTIONS}
        value={filters.status}
        onChange={(v) => updateFilter("status", v)}
        className="w-full min-w-[10rem] sm:w-[12rem]"
      />
      <SelectField
        label={TEXT.taskType}
        options={TASK_TYPE_OPTIONS}
        value={filters.task_type}
        onChange={(v) => updateFilter("task_type", v)}
        className="w-full min-w-[9rem] sm:w-[11rem]"
      />
      <SelectField
        label={TEXT.tag}
        options={tagOptions}
        value={filters.tag_id}
        onChange={(v) => updateFilter("tag_id", v)}
        className="w-full min-w-[10rem] sm:w-[13rem]"
      />
      <DateField
        label={TEXT.fromDate}
        value={filters.from_date}
        max={filters.to_date || undefined}
        onChange={(v) => updateFilter("from_date", v)}
        className="w-full min-w-[9.5rem] sm:w-[11rem]"
      />
      <DateField
        label={TEXT.toDate}
        value={filters.to_date}
        min={filters.from_date || undefined}
        onChange={(v) => updateFilter("to_date", v)}
        className="w-full min-w-[9.5rem] sm:w-[11rem]"
      />
    </div>
  );

  const SecondaryActions = () => (
    <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
      {hasPermission("hrm.tasks", "A") && (
        <button
          type="button"
          onClick={onAssignClick}
          className="inline-flex items-center gap-2 rounded-xl border-2 border-red-600 bg-white px-4 py-2.5 text-xs font-black uppercase tracking-wide text-red-700 shadow-sm transition hover:bg-red-50 active:scale-[0.98]"
        >
          <CheckSquare size={18} className="shrink-0" strokeWidth={2.25} />
          {TEXT.assign}
        </button>
      )}

      {hasPermission("hrm.tasks", "C") && isAllowed && (
        <button
          type="button"
          onClick={onCreateClick}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wide text-white shadow-md shadow-red-100 transition hover:shadow-lg active:scale-[0.98]"
        >
          <Plus size={18} strokeWidth={2.5} />
          {TEXT.register}
        </button>
      )}
    </div>
  );

  const RightToolbar = () => (
    <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
      <SecondaryActions />
      {canExport && (
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setShowMenu((prev) => !prev)}
            title="Tùy chọn khác"
            aria-label="Tùy chọn khác"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600"
          >
            <MoreHorizontal className="h-5 w-5 shrink-0" strokeWidth={2.25} aria-hidden />
          </button>
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 z-50 mt-2 w-[260px] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg"
              >
                {typeof onExportXlsxRequest === "function" && (
                  <button
                    type="button"
                    onClick={() => openExportModal("detail")}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                  >
                    <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={2} aria-hidden />
                    {TEXT.exportExcel}
                  </button>
                )}
                {typeof onExportSummaryXlsxRequest === "function" && (
                  <button
                    type="button"
                    onClick={() => openExportModal("summary")}
                    className="flex w-full items-center gap-2 border-t border-gray-50 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                  >
                    <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={2} aria-hidden />
                    {TEXT.exportSummaryExcel}
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="relative mb-4">
          <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <label
                className="relative flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition-all duration-200 hover:border-red-300 hover:text-red-600 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-100"
                title={TEXT.monthPicker}
              >
                <Calendar size={18} strokeWidth={2.25} className="pointer-events-none" aria-hidden />
                <input
                  type="month"
                  value={monthValue}
                  onChange={handleMonthChange}
                  aria-label={TEXT.monthPicker}
                  className="absolute inset-0 cursor-pointer rounded-xl opacity-0"
                />
              </label>
              <div className="relative min-w-0 flex-1 group">
                <Search
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-red-500"
                  strokeWidth={2.25}
                />
                <input
                  type="text"
                  placeholder={TEXT.searchPlaceholder}
                  value={filters.keyword}
                  onChange={(e) => updateFilter("keyword", e.target.value)}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-3 text-sm font-medium text-gray-800 outline-none transition-all focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100"
                />
              </div>
              <ActionButton icon={RotateCw} onClick={handleRefresh} title={TEXT.refresh} />
              <ActionButton
                icon={SlidersHorizontal}
                onClick={() => setAdvanced(true)}
                title={TEXT.advanced}
                active={hasAdvancedFilters}
                badge={advancedActiveCount}
              />
              {canExport && (
                <div className="relative" ref={menuRef}>
                  <ActionButton
                    icon={FileSpreadsheet}
                    onClick={() => setShowMenu((prev) => !prev)}
                    title="Kết xuất Excel"
                  />
                  <AnimatePresence>
                    {showMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 z-50 mt-2 w-[240px] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg"
                      >
                        {typeof onExportXlsxRequest === "function" && (
                          <button
                            type="button"
                            onClick={() => openExportModal("detail")}
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                          >
                            <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={2} aria-hidden />
                            {TEXT.exportExcel}
                          </button>
                        )}
                        {typeof onExportSummaryXlsxRequest === "function" && (
                          <button
                            type="button"
                            onClick={() => openExportModal("summary")}
                            className="flex w-full items-center gap-2 border-t border-gray-50 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                          >
                            <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={2} aria-hidden />
                            {TEXT.exportSummaryExcel}
                          </button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          <MobileAdvancedFilterSheet
            open={advanced}
            onClose={() => setAdvanced(false)}
            title={TEXT.advancedTitle}
            footer={
              <>
                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    setAdvanced(false);
                  }}
                  className="flex-1 rounded-2xl border border-gray-200 py-4 text-sm font-bold text-gray-600 transition hover:bg-gray-50"
                >
                  {TEXT.clear}
                </button>
                <button
                  type="button"
                  onClick={() => setAdvanced(false)}
                  className="flex-1 rounded-2xl bg-red-600 py-4 text-sm font-black uppercase tracking-wide text-white shadow-lg shadow-red-200 transition hover:bg-red-700"
                >
                  {TEXT.apply}
                </button>
              </>
            }
          >
            <AdvancedFields />
          </MobileAdvancedFilterSheet>
        </div>
        {exportModal}
      </>
    );
  }

  return (
    <>
      <div className="relative mb-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
            <input
              type="month"
              value={monthValue}
              onChange={handleMonthChange}
              aria-label={TEXT.monthPicker}
              title={TEXT.monthPicker}
              className={`h-[42px] w-full min-w-0 max-w-[12.5rem] sm:w-auto ${monthInputClassName}`}
            />
            <div className="relative min-w-0 flex-1 max-w-xl group">
              <Search
                className="absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-red-500"
                strokeWidth={2.25}
              />
              <input
                type="text"
                placeholder={TEXT.searchPlaceholder}
                value={filters.keyword}
                onChange={(e) => updateFilter("keyword", e.target.value)}
                className="w-full rounded-2xl border-2 border-transparent bg-gray-50 py-2.5 pl-11 pr-4 text-sm font-medium text-gray-800 outline-none transition-all focus:border-red-500 focus:bg-white focus:ring-0"
              />
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <ActionButton icon={RotateCw} onClick={handleRefresh} title={TEXT.refresh} />
              <ActionButton
                icon={SlidersHorizontal}
                onClick={() => setAdvanced(!advanced)}
                active={advanced || hasAdvancedFilters}
                badge={advancedActiveCount}
                title={TEXT.advanced}
              />
            </div>
          </div>

          <RightToolbar />
        </div>

        <AnimatePresence>
          {advanced && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="mt-6 border-t border-gray-100 pt-6">
                <AdvancedFields />
                {hasAdvancedFilters && (
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-600 transition hover:bg-gray-50"
                    >
                      {TEXT.clear}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {exportModal}
    </>
  );
};

export default TaskFilter;
